import React, { useEffect, useMemo, useState } from 'react';
import councilorService, { ESFERA_PARLAMENTAR_LABELS } from '../services/councilorService';
import type { CouncilorDTO, EsferaParlamentar, ResponsavelCandidatoDTO } from '../services/councilorService';
import DashboardPagination from '../components/DashboardPagination';
import { UI_THEME } from '../ui/theme';
import { useAuth } from '../context/AuthContext';
import { Permission } from '../types/user.types';
import { toUpperText } from '../utils/formatters';

const ESFERA_OPTIONS: EsferaParlamentar[] = ['MUNICIPAL', 'ESTADUAL', 'FEDERAL'];

const formatCPF = (v: string) => {
  const n = v.replace(/\D/g, '').slice(0, 11);
  if (n.length <= 3) return n;
  if (n.length <= 6) return `${n.slice(0, 3)}.${n.slice(3)}`;
  if (n.length <= 9) return `${n.slice(0, 3)}.${n.slice(3, 6)}.${n.slice(6)}`;
  return `${n.slice(0, 3)}.${n.slice(3, 6)}.${n.slice(6, 9)}-${n.slice(9)}`;
};

type FormData = {
  councilorId: string;
  fullName: string;
  politicalParty: string;
  cpf: string;
  esfera: EsferaParlamentar;
  sequencialEmenda: string;
  situacaoExcepcional: boolean;
  motivoSituacaoExcepcional: string;
  dataSituacaoExcepcional: string;
  bancada: boolean;
  /** CPF digitado do responsável da bancada; resolvido para um id no submit. Vazio = sem responsável. */
  responsavelCpf: string;
};
const emptyForm = (): FormData => ({
  councilorId: '',
  fullName: '',
  politicalParty: '',
  cpf: '',
  esfera: 'MUNICIPAL',
  sequencialEmenda: '',
  situacaoExcepcional: false,
  motivoSituacaoExcepcional: '',
  dataSituacaoExcepcional: '',
  bancada: false,
  responsavelCpf: '',
});

const PAGE_SIZE_OPTIONS = [10, 20, 50];

const CouncilorsPage: React.FC = () => {
  const { hasPermission } = useAuth();
  const canDelete = hasPermission(Permission.DELETE_COUNCILOR);
  const canCreate = hasPermission(Permission.CREATE_COUNCILOR);
  const canEdit = hasPermission(Permission.EDIT_COUNCILOR);
  const [councilors, setCouncilors] = useState<CouncilorDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [partyFilter, setPartyFilter] = useState('Todos');
  const [cpfFilter, setCpfFilter] = useState('Todos');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState<FormData>(emptyForm());
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const isSaas = UI_THEME === 'saas';
  const surfaceClass = 'border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-900/80';
  const heroClass = isSaas
    ? 'border-teal-100 bg-gradient-to-br from-white via-teal-50/70 to-indigo-50 p-6 shadow-sm dark:border-slate-700 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800'
    : 'border-slate-200 bg-gradient-to-br from-white via-slate-50 to-blue-50 p-6 shadow-sm dark:border-slate-700 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800';
  const accentTextClass = isSaas ? 'text-teal-700 dark:text-teal-300' : 'text-blue-700 dark:text-blue-300';
  const bodyTextClass = 'text-slate-600 dark:text-slate-300';
  const labelTextClass = 'text-slate-500 dark:text-slate-400';
  const strongTextClass = 'text-slate-900 dark:text-slate-100';
  const inputClass = 'w-full rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm text-slate-700 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100 dark:focus:ring-blue-900/40';
  const formInputClass = 'w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-700 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100 disabled:bg-slate-50 disabled:text-slate-400 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100 dark:disabled:bg-slate-800 dark:disabled:text-slate-500 dark:focus:ring-blue-900/40';
  const primaryButtonClass = isSaas
    ? 'bg-teal-600 text-white hover:bg-teal-700 dark:bg-teal-500 dark:hover:bg-teal-400'
    : 'bg-blue-700 text-white hover:bg-blue-800 dark:bg-blue-600 dark:hover:bg-blue-500';
  const secondaryButtonClass = 'border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800';

  const load = async () => {
    setError(null);
    setLoading(true);
    try {
      const data = await councilorService.list();
      data.sort((a, b) => a.fullName.localeCompare(b.fullName));
      setCouncilors(data);
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Erro ao carregar parlamentares.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, []);

  // Quem edita ou cria parlamentar precisa escolher o responsável da bancada; os demais não veem a lista.
  const [responsaveis, setResponsaveis] = useState<ResponsavelCandidatoDTO[]>([]);
  useEffect(() => {
    if (!canEdit && !canCreate) return;
    councilorService.listarCandidatosResponsavel().then(setResponsaveis).catch(() => setResponsaveis([]));
  }, [canEdit, canCreate]);
  const nomeDoResponsavel = (id?: number | null) => responsaveis.find((r) => r.id === id)?.nomeCompleto;
  const cpfDoResponsavel = (id?: number | null) => {
    const cpf = responsaveis.find((r) => r.id === id)?.cpf;
    return cpf ? formatCPF(cpf) : '';
  };

  useEffect(() => {
    if (!message) return;
    const timeout = setTimeout(() => setMessage(null), 4000);
    return () => clearTimeout(timeout);
  }, [message]);

  const parties = useMemo(() => {
    const values = Array.from(new Set(councilors.map((c) => (c.politicalParty || '').trim()).filter(Boolean)));
    return values.sort((a, b) => a.localeCompare(b));
  }, [councilors]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return councilors.filter((c) => {
      if (q) {
        const haystack = [c.fullName, c.politicalParty, c.councilorId, c.cpf].filter(Boolean).join(' ').toLowerCase();
        if (!haystack.includes(q)) return false;
      }

      if (partyFilter !== 'Todos' && (c.politicalParty || '') !== partyFilter) return false;
      if (cpfFilter === 'Com CPF' && !c.cpf) return false;
      if (cpfFilter === 'Sem CPF' && c.cpf) return false;

      return true;
    });
  }, [councilors, cpfFilter, partyFilter, query]);

  useEffect(() => {
    setPage(1);
  }, [query, partyFilter, cpfFilter, pageSize, filtered.length]);

  const paginatedCouncilors = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, page, pageSize]);

  const summaryCards = [
    { label: 'Total exibido', value: filtered.length, dot: 'bg-slate-400' },
    { label: 'Com partido', value: filtered.filter((c) => Boolean(c.politicalParty)).length, dot: isSaas ? 'bg-teal-500' : 'bg-blue-600' },
    { label: 'Com CPF', value: filtered.filter((c) => Boolean(c.cpf)).length, dot: 'bg-emerald-500' },
    { label: 'Sem CPF', value: filtered.filter((c) => !c.cpf).length, dot: 'bg-amber-500' },
  ];

  const openCreate = () => {
    setEditingId(null);
    setFormData(emptyForm());
    setFormError('');
    setShowModal(true);
  };

  const openEdit = (c: CouncilorDTO) => {
    setEditingId(c.councilorId);
    setFormData({
      councilorId: c.councilorId,
      fullName: c.fullName,
      politicalParty: c.politicalParty || '',
      cpf: c.cpf ? formatCPF(c.cpf) : '',
      esfera: c.esfera || 'MUNICIPAL',
      sequencialEmenda: c.sequencialEmenda || '',
      situacaoExcepcional: !!c.situacaoExcepcional,
      motivoSituacaoExcepcional: c.motivoSituacaoExcepcional || '',
      dataSituacaoExcepcional: c.dataSituacaoExcepcional || '',
      bancada: !!c.bancada,
      responsavelCpf: cpfDoResponsavel(c.responsavelUsuarioId),
    });
    setFormError('');
    setShowModal(true);
  };

  const clearFilters = () => {
    setQuery('');
    setPartyFilter('Todos');
    setCpfFilter('Todos');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');
    if (formData.situacaoExcepcional && !formData.motivoSituacaoExcepcional.trim()) {
      setFormError('Informe o motivo da situação excepcional.');
      return;
    }
    let responsavelUsuarioId: number | null = null;
    if (formData.bancada) {
      const digitos = formData.responsavelCpf.replace(/\D/g, '');
      if (digitos) {
        const encontrado = responsaveis.find((r) => (r.cpf || '').replace(/\D/g, '') === digitos);
        if (!encontrado) {
          setFormError('Nenhum parlamentar (vereador ativo) encontrado com esse CPF.');
          return;
        }
        responsavelUsuarioId = encontrado.id;
      }
    }
    setSaving(true);
    try {
      const payload = {
        ...formData,
        bancada: formData.bancada,
        responsavelUsuarioId,
        // responsavelCpf é só de tela; o backend recebe o id já resolvido acima. undefined não
        // entra no JSON enviado.
        responsavelCpf: undefined,
        cpf: formData.cpf ? formData.cpf.replace(/\D/g, '') : undefined,
        politicalParty: formData.politicalParty || undefined,
        sequencialEmenda: formData.sequencialEmenda.trim() || undefined,
        motivoSituacaoExcepcional: formData.situacaoExcepcional ? formData.motivoSituacaoExcepcional.trim() : undefined,
        dataSituacaoExcepcional: formData.situacaoExcepcional ? (formData.dataSituacaoExcepcional || undefined) : undefined,
      };
      if (editingId) {
        await councilorService.update(editingId, payload);
        setMessage({ type: 'success', text: 'Parlamentar atualizado com sucesso.' });
      } else {
        await councilorService.create(payload);
        setMessage({ type: 'success', text: 'Parlamentar criado com sucesso.' });
      }
      setShowModal(false);
      await load();
    } catch (err: any) {
      setFormError(err?.response?.data?.error || 'Erro ao salvar parlamentar.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (c: CouncilorDTO) => {
    if (!window.confirm(`Excluir o parlamentar "${c.fullName}"?`)) return;
    try {
      await councilorService.delete(c.councilorId);
      setMessage({ type: 'success', text: 'Parlamentar excluído com sucesso.' });
      await load();
    } catch (err: any) {
      setMessage({ type: 'error', text: err?.response?.data?.error || 'Erro ao excluir parlamentar.' });
    }
  };

  return (
    <div className="w-full space-y-6">
      <div className={`rounded-3xl border ${heroClass}`}>
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div className="space-y-2">
            <p className={`text-xs font-semibold uppercase tracking-[0.2em] ${accentTextClass}`}>
              {isSaas ? 'Representantes' : 'Gestão'}
            </p>
            <div>
              <h1 className={`text-2xl font-bold tracking-tight sm:text-3xl ${strongTextClass}`}>Parlamentares</h1>
              <p className={`mt-2 max-w-3xl text-sm leading-6 ${bodyTextClass}`}>
                {isSaas
                  ? 'Mantenha vereadores, partidos e documentos prontos para vínculo rápido nas emendas.'
                  : 'Gerencie o cadastro de vereadores e parlamentares vinculados às emendas, mantendo identificação, partido e CPF organizados.'}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {canCreate && (
              <button
                type="button"
                onClick={openCreate}
                className={`inline-flex items-center justify-center rounded-xl px-4 py-2 text-sm font-semibold transition ${primaryButtonClass}`}
              >
                Novo parlamentar
              </button>
            )}
            <button
              type="button"
              onClick={clearFilters}
              className={`inline-flex items-center justify-center rounded-xl px-4 py-2 text-sm font-medium transition ${secondaryButtonClass}`}
            >
              Limpar filtros
            </button>
          </div>
        </div>

        <div className={`mt-5 overflow-hidden rounded-2xl border ${surfaceClass}`}>
          <div className="grid divide-y divide-slate-200 dark:divide-slate-700 sm:grid-cols-2 sm:divide-x sm:divide-y-0 xl:grid-cols-4">
            {summaryCards.map((card) => (
              <div key={card.label} className="min-h-[72px] px-4 py-3">
                <p className={`flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] ${labelTextClass}`}>
                  <span className={`h-1.5 w-1.5 rounded-full ${card.dot}`} />
                  {card.label}
                </p>
                <p className={`mt-2 text-2xl font-bold ${strongTextClass}`}>{card.value}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className={`rounded-3xl border p-5 ${surfaceClass}`}>
        <div className="grid gap-3 xl:grid-cols-[minmax(0,2.2fr)_220px_180px_auto]">
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscar por nome, partido, CPF ou ID do parlamentar..."
            className={inputClass}
          />
          <select
            value={partyFilter}
            onChange={(e) => setPartyFilter(e.target.value)}
            className={inputClass}
          >
            <option value="Todos">Todos os partidos</option>
            {parties.map((party) => (
              <option key={party} value={party}>{party}</option>
            ))}
          </select>
          <select
            value={cpfFilter}
            onChange={(e) => setCpfFilter(e.target.value)}
            className={inputClass}
          >
            <option value="Todos">CPF: todos</option>
            <option value="Com CPF">Com CPF</option>
            <option value="Sem CPF">Sem CPF</option>
          </select>
          <div className="flex items-center justify-end">
            <button
              type="button"
              onClick={load}
              className={`inline-flex items-center justify-center rounded-xl px-4 py-2 text-sm font-medium transition ${secondaryButtonClass}`}
            >
              Recarregar
            </button>
          </div>
        </div>
      </div>

      {message && (
        <div className={`rounded-2xl border px-4 py-3 text-sm shadow-sm ${
          message.type === 'success'
            ? 'border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-900/60 dark:bg-emerald-950/30 dark:text-emerald-200'
            : 'border-red-200 bg-red-50 text-red-800 dark:border-red-900/60 dark:bg-red-950/30 dark:text-red-200'
        }`}>
          {message.text}
        </div>
      )}

      {loading ? (
        <div className={`rounded-3xl border p-12 text-center ${surfaceClass} ${labelTextClass}`}>
          Carregando parlamentares...
        </div>
      ) : error ? (
        <div className="rounded-3xl border border-red-200 bg-red-50 p-8 text-center shadow-sm dark:border-red-900/60 dark:bg-red-950/30">
          <p className="font-semibold text-red-800 dark:text-red-200">Erro ao carregar parlamentares</p>
          <p className="mt-2 text-sm text-red-700 dark:text-red-300">{error}</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className={`rounded-3xl border p-12 text-center ${surfaceClass}`}>
          <p className={`text-lg font-semibold ${strongTextClass}`}>
            {councilors.length === 0 ? 'Nenhum parlamentar cadastrado ainda.' : 'Nenhum parlamentar encontrado com os filtros selecionados.'}
          </p>
          {canCreate && (
          <button
            type="button"
            onClick={openCreate}
            className={`mt-5 inline-flex items-center justify-center rounded-xl px-4 py-2 text-sm font-semibold transition ${primaryButtonClass}`}
          >
            Criar primeiro parlamentar
          </button>
          )}
        </div>
      ) : (
        <div className={`overflow-hidden rounded-3xl border ${surfaceClass}`}>
          <table className="w-full text-sm">
            <thead className="bg-slate-50 dark:bg-slate-800/80">
              <tr className="border-b border-slate-200 dark:border-slate-700">
                <th className={`px-5 py-4 text-left font-semibold ${bodyTextClass}`}>Parlamentar</th>
                <th className={`px-4 py-4 text-left font-semibold ${bodyTextClass}`}>Partido</th>
                <th className={`px-4 py-4 text-left font-semibold ${bodyTextClass}`}>Esfera</th>
                <th className={`px-4 py-4 text-left font-semibold ${bodyTextClass}`}>Seq. emendas</th>
                <th className={`px-4 py-4 text-left font-semibold ${bodyTextClass}`}>CPF</th>
                <th className={`px-4 py-4 text-left font-semibold ${bodyTextClass}`}>ID</th>
                <th className={`px-5 py-4 text-right font-semibold ${bodyTextClass}`}>Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {paginatedCouncilors.map((councilor) => (
                <tr key={councilor.councilorId} className="hover:bg-slate-50/70 dark:hover:bg-slate-800/60">
                  <td className="px-5 py-4 align-top">
                    <div className="space-y-1">
                      <p className={`font-semibold ${strongTextClass}`}>{councilor.fullName}</p>
                      <p className={`text-xs leading-5 ${labelTextClass}`}>Cadastro vinculado ao identificador {councilor.councilorId}.</p>
                      {councilor.situacaoExcepcional && (
                        <span
                          title={councilor.motivoSituacaoExcepcional || 'Situação excepcional'}
                          className="inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-800 dark:border-amber-900/60 dark:bg-amber-950/30 dark:text-amber-200"
                        >
                          Situação excepcional{councilor.motivoSituacaoExcepcional ? `: ${councilor.motivoSituacaoExcepcional}` : ''}
                        </span>
                      )}
                      {councilor.bancada && (
                        <span className="inline-flex items-center rounded-full border border-teal-200 bg-teal-50 px-2 py-0.5 text-[11px] font-semibold text-teal-800 dark:border-teal-900/60 dark:bg-teal-950/30 dark:text-teal-200">
                          Bancada · {nomeDoResponsavel(councilor.responsavelUsuarioId) ?? (councilor.responsavelUsuarioId ? 'com responsável' : 'sem responsável')}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className={`px-4 py-4 align-top ${bodyTextClass}`}>{councilor.politicalParty || 'Não informado'}</td>
                  <td className={`px-4 py-4 align-top ${bodyTextClass}`}>{councilor.esfera ? ESFERA_PARLAMENTAR_LABELS[councilor.esfera] : 'Municipal'}</td>
                  <td className={`px-4 py-4 align-top font-mono ${bodyTextClass}`}>{councilor.sequencialEmenda || '—'}</td>
                  <td className={`px-4 py-4 align-top font-mono ${bodyTextClass}`}>{councilor.cpf ? formatCPF(councilor.cpf) : 'Não informado'}</td>
                  <td className={`px-4 py-4 align-top ${bodyTextClass}`}>{councilor.councilorId}</td>
                  <td className="px-5 py-4 align-top">
                    <div className="flex justify-end gap-2">
                      {canEdit && (
                        <button
                          type="button"
                          onClick={() => openEdit(councilor)}
                          className={`rounded-xl px-3 py-2 text-xs font-semibold transition ${primaryButtonClass}`}
                        >
                          Editar
                        </button>
                      )}
                      {canDelete && (
                        <button
                          type="button"
                          onClick={() => handleDelete(councilor)}
                          className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-xs font-medium text-red-700 transition hover:bg-red-100 dark:border-red-900/60 dark:bg-red-950/30 dark:text-red-200 dark:hover:bg-red-950/50"
                        >
                          Excluir
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <DashboardPagination
            page={page}
            pageSize={pageSize}
            totalItems={filtered.length}
            onPageChange={setPage}
            onPageSizeChange={(value) => {
              setPageSize(value);
              setPage(1);
            }}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            itemLabel="parlamentares"
          />
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 px-4 py-6">
          <div className="w-full max-w-xl rounded-3xl border border-slate-200 bg-white p-6 shadow-2xl dark:border-slate-700 dark:bg-slate-900">
            <div className="mb-5">
              <p className={`text-xs font-semibold uppercase tracking-[0.18em] ${accentTextClass}`}>Cadastro</p>
              <h2 className={`mt-1 text-xl font-bold ${strongTextClass}`}>
                {editingId ? 'Editar parlamentar' : 'Novo parlamentar'}
              </h2>
              <p className={`mt-1 text-sm ${labelTextClass}`}>
                Preencha os dados principais do parlamentar para manter o vínculo com emendas e usuários consistente.
              </p>
            </div>

            {formError && (
              <div className="mb-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900/60 dark:bg-red-950/30 dark:text-red-200">
                {formError}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="rounded-xl border border-slate-200 p-3 dark:border-slate-700">
                <label className="flex items-center gap-2 text-sm font-medium">
                  <input
                    type="checkbox"
                    checked={formData.bancada}
                    onChange={(e) => setFormData((p) => ({
                      ...p,
                      bancada: e.target.checked,
                      responsavelCpf: e.target.checked ? p.responsavelCpf : '',
                    }))}
                  />
                  <span className={bodyTextClass}>Bancada</span>
                </label>
                <p className={`mt-1 text-xs ${labelTextClass}`}>
                  {formData.bancada
                    ? 'Informe o CPF do responsável da bancada.'
                    : 'Marque quando este cadastro representa uma bancada. O limite e a seleção de projetos seguem as mesmas regras de um parlamentar.'}
                </p>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className={`mb-1 block text-sm font-medium ${bodyTextClass}`}>ID do parlamentar *</label>
                  <input
                    type="text"
                    required
                    value={formData.councilorId}
                    onChange={(e) => setFormData((p) => ({ ...p, councilorId: e.target.value }))}
                    disabled={!!editingId}
                    className={formInputClass}
                    placeholder="Ex: PAR001"
                  />
                </div>
                <div>
                  <label className={`mb-1 block text-sm font-medium ${bodyTextClass}`}>
                    {formData.bancada ? 'CPF do responsável' : 'CPF'}
                  </label>
                  {formData.bancada ? (
                    <input
                      type="text"
                      value={formData.responsavelCpf}
                      onChange={(e) => setFormData((p) => ({ ...p, responsavelCpf: formatCPF(e.target.value) }))}
                      className={formInputClass}
                      placeholder="000.000.000-00"
                      maxLength={14}
                    />
                  ) : (
                    <input
                      type="text"
                      value={formData.cpf}
                      onChange={(e) => setFormData((p) => ({ ...p, cpf: formatCPF(e.target.value) }))}
                      className={formInputClass}
                      placeholder="000.000.000-00"
                      maxLength={14}
                    />
                  )}
                </div>
              </div>

              <div>
                <label className={`mb-1 block text-sm font-medium ${bodyTextClass}`}>Nome completo *</label>
                <input
                  type="text"
                  required
                  value={formData.fullName}
                  onChange={(e) => setFormData((p) => ({ ...p, fullName: toUpperText(e.target.value) }))}
                  className={formInputClass}
                  placeholder="Nome completo do parlamentar"
                />
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className={`mb-1 block text-sm font-medium ${bodyTextClass}`}>Partido político</label>
                  <input
                    type="text"
                    value={formData.politicalParty}
                    onChange={(e) => setFormData((p) => ({ ...p, politicalParty: toUpperText(e.target.value) }))}
                    className={formInputClass}
                    placeholder="Ex: MDB, PL, PT..."
                  />
                </div>
                <div>
                  <label className={`mb-1 block text-sm font-medium ${bodyTextClass}`}>Esfera *</label>
                  <select
                    value={formData.esfera}
                    onChange={(e) => setFormData((p) => ({ ...p, esfera: e.target.value as EsferaParlamentar }))}
                    className={formInputClass}
                  >
                    {ESFERA_OPTIONS.map((op) => (
                      <option key={op} value={op}>{ESFERA_PARLAMENTAR_LABELS[op]}</option>
                    ))}
                  </select>
                  <p className={`mt-1 text-xs ${labelTextClass}`}>
                    Limites parlamentares aplicam-se somente à esfera municipal.
                  </p>
                </div>
                <div>
                  <label className={`mb-1 block text-sm font-medium ${bodyTextClass}`}>Sequencial das emendas</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength={3}
                    value={formData.sequencialEmenda}
                    onChange={(e) => setFormData((p) => ({ ...p, sequencialEmenda: e.target.value.replace(/\D/g, '') }))}
                    className={formInputClass}
                    placeholder="Ex: 001"
                  />
                  <p className={`mt-1 text-xs ${labelTextClass}`}>
                    Número fixo do parlamentar no código das emendas municipais de 2027 em diante. Não se repete entre parlamentares.
                  </p>
                </div>
              </div>

              <div className="rounded-xl border border-slate-200 p-3 dark:border-slate-700">
                <label className="flex items-center gap-2 text-sm font-medium">
                  <input
                    type="checkbox"
                    checked={formData.situacaoExcepcional}
                    onChange={(e) => setFormData((p) => ({ ...p, situacaoExcepcional: e.target.checked }))}
                  />
                  <span className={bodyTextClass}>Situação excepcional (impede uso do próprio saldo)</span>
                </label>
                <p className={`mt-1 text-xs ${labelTextClass}`}>
                  Marque quando o parlamentar não está mais em condição de usar o próprio saldo de emendas,
                  por qualquer motivo excepcional. Isso habilita a Câmara a redistribuir emendas devolvidas
                  dele para outro parlamentar.
                </p>

                {formData.situacaoExcepcional && (
                  <div className="mt-3 grid gap-4 sm:grid-cols-2">
                    <div>
                      <label className={`mb-1 block text-sm font-medium ${bodyTextClass}`}>Motivo *</label>
                      <input
                        type="text"
                        required
                        value={formData.motivoSituacaoExcepcional}
                        onChange={(e) => setFormData((p) => ({ ...p, motivoSituacaoExcepcional: toUpperText(e.target.value) }))}
                        className={formInputClass}
                        placeholder="Ex: Renúncia de mandato, Cassação, Afastamento definitivo..."
                      />
                    </div>
                    <div>
                      <label className={`mb-1 block text-sm font-medium ${bodyTextClass}`}>Data</label>
                      <input
                        type="date"
                        value={formData.dataSituacaoExcepcional}
                        onChange={(e) => setFormData((p) => ({ ...p, dataSituacaoExcepcional: e.target.value }))}
                        className={formInputClass}
                      />
                    </div>
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className={`rounded-xl px-4 py-2 text-sm font-medium transition ${secondaryButtonClass}`}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className={`rounded-xl px-4 py-2 text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-50 ${primaryButtonClass}`}
                >
                  {saving ? 'Salvando...' : editingId ? 'Atualizar' : 'Criar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CouncilorsPage;
